import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL:         BASE,
  withCredentials: true,
  headers:         { 'Content-Type': 'application/json' },
  timeout:         30000,
});

// ── Request interceptor: attach token if stored ───────────────
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('mi_hrm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor: handle 401 → refresh or redirect ───
let refreshing = false;
let queue = [];

api.interceptors.response.use(
  (res) => {
    const method = res.config?.method?.toLowerCase();
    if (typeof window !== 'undefined' && ['post', 'put', 'delete', 'patch'].includes(method)) {
      window.dispatchEvent(new CustomEvent('refetch-sidebar-counts'));
    }
    return res;
  },
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      if (refreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then(() => api(original));
      }
      original._retry = true;
      refreshing = true;
      try {
        const { data } = await axios.post(`${BASE}/auth/refresh`, {}, { withCredentials: true });
        if (data.access_token) {
          sessionStorage.setItem('mi_hrm_token', data.access_token);
          queue.forEach(({ resolve }) => resolve());
          queue = [];
          original.headers.Authorization = `Bearer ${data.access_token}`;
          return api(original);
        }
      } catch {
        sessionStorage.removeItem('mi_hrm_token');
        queue.forEach(({ reject }) => reject(err));
        queue = [];
        
        // Only redirect once and ONLY if we are NOT already on a public page.
        // For candidates, / and /jobs and /jobs/:id are public!
        const path = window.location.pathname;
        const isPublicPath = path === '/login' || path.endsWith('/login') || path === '/' || path === '/jobs' || path.startsWith('/jobs/');
        if (!window._mi_hrm_redirecting && !isPublicPath) {
          window._mi_hrm_redirecting = true;
          window.location.href = '/login';
        }
      } finally {
        refreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Typed API helpers ──────────────────────────────────────────
export const candidateApi = {
  register:            (d) => api.post('/candidate-recruitment/register', d),
  login:               (d) => api.post('/candidate-recruitment/login', d),
  logout:              ()  => api.post('/candidate-recruitment/logout'),
  getProfile:          ()  => api.get('/candidate-recruitment/profile'),
  updateProfile:       (d) => api.put('/candidate-recruitment/profile', d),
  uploadDoc:           (formData) => api.post('/candidate-recruitment/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listJobs:            ()  => api.get('/candidate-recruitment/jobs'),
  getJob:              (id) => api.get(`/candidate-recruitment/jobs/${id}`),
  applyJob:            (id, d) => api.post(`/candidate-recruitment/jobs/${id}/apply`, d),
  myApplications:      ()  => api.get('/candidate-recruitment/my-applications'),
  getOnboardingStatus: ()  => api.get('/candidate-recruitment/onboarding/status'),
  submitOnboarding:    (d) => api.post('/candidate-recruitment/onboarding/submit-details', d),
};

export const authApi = {
  login:   (d)    => api.post('/auth/login', d),
  logout:  ()     => api.post('/auth/logout'),
  me:      ()     => api.get('/auth/me'),
  refresh: ()     => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  getPasswordPolicy: () => api.get('/auth/password-policy'),
};

export const setupApi = {
  check:  ()  => api.get('/setup'),
  submit: (d) => api.post('/setup', d),
};

export const dashApi    = { 
  get: (params) => api.get('/dashboard', { params }), 
  getCelebrations: (params) => api.get('/dashboard/celebrations', { params }),
  sendMessage: (d) => api.post('/dashboard/celebrations/send-message', d),
  notifySupervisor: (d) => api.post('/dashboard/celebrations/notify-supervisor', d),
  scheduleReview: (d) => api.post('/dashboard/celebrations/schedule-review', d)
};
export const empApi     = {
  list:   (p) => api.get('/employees', { params: p }),
  lookup: () => api.get('/employees/lookup'),
  get:    (id) => api.get(`/employees/${encodeURIComponent(id)}`),
  create: (d)  => api.post('/employees', d),
  update: (id,d) => api.put(`/employees/${encodeURIComponent(id)}`, d),
  updateSalary: (id, d) => api.put(`/employees/${encodeURIComponent(id)}/salary`, d),
  remove: (id)  => api.delete(`/employees/${encodeURIComponent(id)}`),
  hardRemove: (id) => api.delete(`/employees/${encodeURIComponent(id)}/hard`),
  bulkImport: (d) => api.post('/employees/bulk', { employees: d }),
  getLeaveBalances: (id) => api.get(`/employees/${encodeURIComponent(id)}/leave-balances`),
};
export const deptApi    = {
  list:   (p) => api.get('/departments', { params: p }),
  get:    (id) => api.get(`/departments/${encodeURIComponent(id)}`),
  create: (d) => api.post('/departments', d),
  update: (id,d) => api.put(`/departments/${encodeURIComponent(id)}`, d),
  remove: (id) => api.delete(`/departments/${encodeURIComponent(id)}`),
  bulkImport: (d) => api.post('/departments/bulk', { departments: d }),
};
export const directorateApi = {
  list:   (p) => api.get('/directorates', { params: p }),
  get:    (id) => api.get(`/directorates/${encodeURIComponent(id)}`),
  create: (d) => api.post('/directorates', d),
  update: (id,d) => api.put(`/directorates/${encodeURIComponent(id)}`, d),
  remove: (id) => api.delete(`/directorates/${encodeURIComponent(id)}`),
};
export const leaveApi   = {
  list:   (p) => api.get('/leave', { params: p }),
  create: (d) => api.post('/leave', d),
  review: (id,d) => api.patch(`/leave/${encodeURIComponent(id)}/review`, d),
  getEntitlements: (empId) => api.get(`/leave/entitlements/${encodeURIComponent(empId)}`),
  updateEntitlement: (empId, type, d) => api.put(`/leave/entitlements/${encodeURIComponent(empId)}/${encodeURIComponent(type)}`, d),
  getTypes:     () => api.get('/leave/types'),
  saveType:      (d) => api.post('/leave/types', d),
  updateType:    (id, d) => api.put(`/leave/types/${id}`, d),
  removeType:    (id) => api.delete(`/leave/types/${id}`),
  getWorkflows:  () => api.get('/leave/workflows'),
  createWorkflow:(d) => api.post('/leave/workflows', d),
  updateWorkflow:(id, d) => api.put(`/leave/workflows/${id}`, d),
  removeWorkflow:(id) => api.delete(`/leave/workflows/${id}`),
  getWorkflowSteps: (id) => api.get(`/leave/workflows/${id}/steps`),
  getHolidays:   () => api.get('/leave/holidays'),
  saveHoliday:   (d) => api.post('/leave/holidays', d),
  removeHoliday: (id) => api.delete(`/leave/holidays/${id}`),
  removeRequest: (id) => api.delete(`/leave/${id}`),
  cancel: (id) => api.post(`/leave/${encodeURIComponent(id)}/cancel`),
  getPolicy:     () => api.get('/leave/policy'),
  updatePolicy:  (d) => api.post('/leave/policy', d),
};
export const attApi     = {
  list:    (p) => api.get('/attendance', { params: p }),
  summary: (p) => api.get('/attendance/summary', { params: p }),
  update:  (id, d) => api.put(`/attendance/${id}`, d),
  listCorrections: (p) => api.get('/attendance/corrections/list', { params: p }),
  myCorrections:   () => api.get('/attendance/corrections/my'),
  submitCorrection: (d) => api.post('/attendance/corrections', d),
  approveCorrection: (id, d) => api.patch(`/attendance/corrections/${id}/approve`, d),
  rejectCorrection:  (id) => api.patch(`/attendance/corrections/${id}/reject`),
  essDashboard:      () => api.get('/attendance/ess/dashboard'),
};

export const biometricApi = {
  listDevices:           () => api.get('/biometric/devices'),
  createDevice:          (d) => api.post('/biometric/devices', d),
  updateDevice:          (id, d) => api.put(`/biometric/devices/${id}`, d),
  deleteDevice:          (id) => api.delete(`/biometric/devices/${id}`),
  syncDevice:            (id) => api.post(`/biometric/devices/${id}/sync`),
  listMappings:          () => api.get('/biometric/mappings'),
  createMapping:         (d) => api.post('/biometric/mappings', d),
  deleteMapping:         (id) => api.delete(`/biometric/mappings/${id}`),
  listRawLogs:           () => api.get('/biometric/raw-logs'),
  listSyncLogs:          () => api.get('/biometric/sync-logs'),
  simulatePunches:       (d) => api.post('/biometric/simulate-punches', d),
  listShifts:            () => api.get('/biometric/shifts'),
  createShift:           (d) => api.post('/biometric/shifts', d),
  updateShift:           (id, d) => api.put(`/biometric/shifts/${id}`, d),
  listShiftAssignments:  () => api.get('/biometric/shift-assignments'),
  createShiftAssignment: (d) => api.post('/biometric/shift-assignments', d),
  deleteShiftAssignment: (id) => api.delete(`/biometric/shift-assignments/${id}`),
  readinessCheck:        (id) => api.get(`/payroll/runs/${id}/readiness`),
  
  // New Biometric Import / Upload endpoints
  uploadPreview:         (formData) => api.post('/biometric/upload-preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadConfirm:         (d) => api.post('/biometric/upload-confirm', d),
  confirmImportBatch:    (batchId) => api.post(`/biometric/import-batch/${batchId}/confirm`),
  reprocessImportBatch:  (batchId) => api.post(`/biometric/import-batch/${batchId}/reprocess`),
  deleteImportBatch:     (batchId) => api.delete(`/biometric/import-batch/${batchId}`),
  listImportBatches:     () => api.get('/biometric/import-batches'),
  listBatchLogs:         (batchId) => api.get(`/biometric/import-batches/${batchId}/logs`),
  matchRawLog:           (id, empId) => api.post(`/biometric/raw-logs/${id}/match`, { empId })
};
export const payrollApi = {
  runs:             (p) => api.get('/payroll/runs', { params: p }),
  createRun:        (d) => api.post('/payroll/runs', d),
  editRun:          (id, d) => api.put(`/payroll/runs/${id}`, d),
  deleteRun:        (id) => api.delete(`/payroll/runs/${id}`),
  payslips:         (id) => api.get(`/payroll/runs/${id}/payslips`),
  compute:          (id) => api.post(`/payroll/runs/${id}/compute`),
  submit:           (id) => api.patch(`/payroll/runs/${id}/submit`),
  approve:          (id) => api.patch(`/payroll/runs/${id}/approve`),
  lock:             (id) => api.patch(`/payroll/runs/${id}/lock`),
  pay:              (id) => api.patch(`/payroll/runs/${id}/pay`),
  reopen:           (id, d) => api.patch(`/payroll/runs/${id}/reopen`, d),
  updateAdjustment: (slipId, d) => api.put(`/payroll/payslips/${slipId}`, d),
  myPayslips:       ()   => api.get('/payroll/my-payslips'),
  listComponents:   (p)  => api.get('/payroll/components', { params: p }),
  createComponent: (d)  => api.post('/payroll/components', d),
  updateComponent: (id, d) => api.put(`/payroll/components/${id}`, d),
  deleteComponent: (id)  => api.delete(`/payroll/components/${id}`),
  bulkAssignComponent: (d) => api.post('/payroll/components/bulk', d),
  quickFixSave: (d) => api.post('/payroll/quick-fix', d)
};
export const perfApi    = {
  cycles:           () => api.get('/performance/cycles'),
  createCycle:      (d) => api.post('/performance/cycles', d),
  updateCycle:      (id, d) => api.put(`/performance/cycles/${id}`, d),
  
  // KPI Library
  getKpiLibrary:    () => api.get('/performance/kpi-library'),
  createKpiLibrary: (d) => api.post('/performance/kpi-library', d),
  updateKpiLibrary: (id, d) => api.put(`/performance/kpi-library/${id}`, d),
  deleteKpiLibrary: (id) => api.delete(`/performance/kpi-library/${id}`),

  // Rating Scale
  getRatingScale:   () => api.get('/performance/rating-scale'),
  updateRatingScale:(scales) => api.put('/performance/rating-scale', { scales }),

  // Participant details & appraisal lifecycle
  getParticipants:  (cycleId) => api.get(`/performance/cycles/${cycleId}/participants`),
  getMyAppraisal:   (cycleId, empId) => api.get(`/performance/cycles/${cycleId}/my-appraisal`, { params: empId ? { emp_id: empId } : {} }),
  addCustomKpi:     (cycleId, d) => api.post(`/performance/cycles/${cycleId}/kpis`, d),
  deleteCustomKpi:  (cycleId, kpiId) => api.delete(`/performance/cycles/${cycleId}/kpis/${kpiId}`),
  submitKpis:       (cycleId, empId) => api.post(`/performance/cycles/${cycleId}/submit-kpis`, { emp_id: empId }),
  selfAssess:       (cycleId, d) => api.post(`/performance/cycles/${cycleId}/self-assess`, d),
  approve:          (cycleId, d) => api.post(`/performance/cycles/${cycleId}/approve`, d),
  reject:           (cycleId, d) => api.post(`/performance/cycles/${cycleId}/reject`, d),
  getApprovals:     () => api.get('/performance/approvals'),
  getHistory:       (empId) => api.get(`/performance/history/${encodeURIComponent(empId)}`),

  // Workflows
  getWorkflows:     () => api.get('/performance/workflows'),
  createWorkflow:   (d) => api.post('/performance/workflows', d),
  deleteWorkflow:   (id) => api.delete(`/performance/workflows/${id}`),

  // Reports
  getDeptReport:    () => api.get('/performance/reports/department'),
  getEmpReport:     () => api.get('/performance/reports/employee'),
  getProbationReport:() => api.get('/performance/reports/probation'),
  getLowPerformanceReport:() => api.get('/performance/reports/low-performance'),
  getTrainingNeedsReport: () => api.get('/performance/reports/training-needs'),
  getPromotionsReport:() => api.get('/performance/reports/promotions'),
  getConfirmationsReport:() => api.get('/performance/reports/confirmations'),
  getAnnualSummaryReport:() => api.get('/performance/reports/annual-summary'),

  // Legacy/other methods
  kpis:      (p) => api.get('/performance/kpis', { params: p }),
  createKPIs:(d) => api.post('/performance/kpis', d),
  reviewKPI: (id,d) => api.patch(`/performance/kpis/${id}/review`, d),
  assess:    (id,d) => api.post(`/performance/kpis/${id}/assess`, d),
  pending:   () => api.get('/performance/kpis/pending'),
  audit:     (empId) => api.get(`/performance/audit/${encodeURIComponent(empId)}`),
  listAppraisals: (p) => api.get('/performance/appraisals', { params: p }),
  createAppraisal: (d) => api.post('/performance/appraisals', d),
};
export const reportsApi = { 
  get: (p) => api.get('/reports', { params: p }), 
  getDetailed: (p) => api.get('/reports/detailed', { params: p }) 
};
export const auditApi   = { list: (p) => api.get('/audit', { params: p }) };
export const userApi    = { 
  list: (p) => api.get('/users', { params: p }),
  getStats: () => api.get('/users/stats'),
  update: (id, d) => api.put(`/users/${id}`, d),
  remove: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, password) => api.post(`/users/${id}/reset-password`, { password }),
  sync: () => api.post('/users/sync'),
}; 
export const rbacApi = {
  getRoles: () => api.get('/rbac/roles'),
  createRole: (d) => api.post('/rbac/roles', d),
  getPermissions: (name) => api.get(`/rbac/roles/${name}/permissions`),
  savePermissions: (name, permissions) => api.post(`/rbac/roles/${name}/permissions`, { permissions }),
};
export const systemApi  = { 
  getConfig: () => api.get('/system/config'),
  getPublicConfig: () => api.get('/public-config'),
  saveConfig: (settings) => api.post('/system/config', { settings }),
  testEmail: (to) => api.post('/system/test-email', { to }),
  validatePassword: (password) => api.post('/system/validate-password', { password }),
  getRoleLevels: () => api.get('/system/role-levels'),
  createRoleLevel: (d) => api.post('/system/role-levels', d),
  updateRoleLevel: (id, d) => api.put(`/system/role-levels/${id}`, d),
  deleteRoleLevel: (id) => api.delete(`/system/role-levels/${id}`),
  getReportingLines: () => api.get('/system/reporting-lines'),
  createReportingLine: (d) => api.post('/system/reporting-lines', d),
  deleteReportingLine: (id) => api.delete(`/system/reporting-lines/${id}`),
  getEscalationRules: () => api.get('/system/escalation-rules'),
  createEscalationRule: (d) => api.post('/system/escalation-rules', d),
  updateEscalationRule: (id, d) => api.put(`/system/escalation-rules/${id}`, d),
  deleteEscalationRule: (id) => api.delete(`/system/escalation-rules/${id}`),
  getHrNotificationRules: () => api.get('/system/hr-notification-rules'),
  updateHrNotificationRule: (id, d) => api.put(`/system/hr-notification-rules/${id}`, d),
  getDelegationRules: () => api.get('/system/delegation-rules'),
  createDelegationRule: (d) => api.post('/system/delegation-rules', d),
  deleteDelegationRule: (id) => api.delete(`/system/delegation-rules/${id}`),
};


export const posApi = {
  list:      (deptId) => api.get('/positions', { params: { dept_id: deptId } }),
  getGrades: () => api.get('/positions/grades'),
  create:    (d)      => api.post('/positions', d),
  update:    (id, d)  => api.put(`/positions/${id}`, d),
  remove:    (id)      => api.delete(`/positions/${id}`),
};

export const jobTitleApi = {
  list:   (params) => api.get('/job-titles', { params }),
  create: (d)      => api.post('/job-titles', d),
  update: (id, d)  => api.put(`/job-titles/${id}`, d),
  remove: (id)      => api.delete(`/job-titles/${id}`),
};


export const docApi = {
  listAll: (p)       => api.get('/employee-docs', { params: p }),
  list:   (empId)   => api.get(`/employee-docs/${encodeURIComponent(empId)}`),
  upload: (empId, d) => api.post(`/employee-docs/${encodeURIComponent(empId)}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (docId)   => api.delete(`/employee-docs/item/${docId}`),
};

export const recruitmentApi = {
  getDashboard:   () => api.get('/recruitment/dashboard'),
  listPostings:   (p) => api.get('/recruitment/postings', { params: p }),
  createPosting:  (d) => api.post('/recruitment/postings', d),
  updatePosting:  (id, d) => api.put(`/recruitment/postings/${id}`, d),
  removePosting:  (id) => api.delete(`/recruitment/postings/${id}`),
  listVacancies:  (p) => api.get('/recruitment/vacancies', { params: p }),
  createVacancy:  (d) => api.post('/recruitment/vacancies', d),
  updateVacancy:  (id, d) => api.put(`/recruitment/vacancies/${id}`, d),
  approveVacancy: (id, d) => api.post(`/recruitment/vacancies/${id}/approve`, d),
  listApplicants: (p) => api.get('/recruitment/applicants', { params: p }),
  createApplicant:(d) => api.post('/recruitment/applicants', d),
  updateApplicant:(id, d) => api.put(`/recruitment/applicants/${id}`, d),
  updateApplicantShortlist: (id, d) => api.put(`/recruitment/applicants/${id}/shortlist-score`, d),
  verifyApplicantDocs: (id, d) => api.put(`/recruitment/applicants/${id}/documents/verify`, d),
  listInterviews: () => api.get('/recruitment/interviews'),
  createInterview: (d) => api.post('/recruitment/interviews', d),
  updateInterview: (id, d) => api.put(`/recruitment/interviews/${id}`, d),
  getInterviewScores: (id) => api.get(`/recruitment/interviews/${id}/scores`),
  submitInterviewScore: (id, d) => api.post(`/recruitment/interviews/${id}/scores`, d),
  getVacancyRanking: (id) => api.get(`/recruitment/vacancies/${id}/ranking`),
  listOffers:     () => api.get('/recruitment/offers'),
  createOffer:    (d) => api.post('/recruitment/offers', d),
  updateOffer:    (id, d) => api.put(`/recruitment/offers/${id}`, d),
  getOnboardingDashboard: () => api.get('/recruitment/onboarding/dashboard'),
  listOnboardingCandidates: () => api.get('/recruitment/onboarding/candidates'),
  convertCandidate: (d) => api.post('/recruitment/onboarding/convert', d),
  listOnboardings: () => api.get('/recruitment/onboarding'),
  getOnboardingChecklist: (id) => api.get(`/recruitment/onboarding/${id}`),
  updateOnboardingChecklist: (id, d) => api.put(`/recruitment/onboarding/checklists/${id}`, d),
  confirmOnboarding: (id, d) => api.post(`/recruitment/onboarding/checklists/${id}/confirm`, d),
};

export const trainingApi = {
  listCourses:    (p) => api.get('/training/courses', { params: p }),
  createCourse:   (d) => api.post('/training/courses', d),
  updateCourse:   (id, d) => api.put(`/training/courses/${id}`, d),
  removeCourse:   (id) => api.delete(`/training/courses/${id}`),
  listEnrollments:(p) => api.get('/training/enrollments', { params: p }),
  enroll:         (d) => api.post('/training/enrollments', d),
  updateEnrollment:(id, d) => api.put(`/training/enrollments/${id}`, d),
  removeEnrollment:(id) => api.delete(`/training/enrollments/${id}`),
};

export const grievanceApi = {
  listDisciplinary: (p) => api.get('/grievances/disciplinary', { params: p }),
  createDisciplinary:(d) => api.post('/grievances/disciplinary', d),
  updateDisciplinary:(id, d) => api.put(`/grievances/disciplinary/${id}`, d),
  removeDisciplinary:(id) => api.delete(`/grievances/disciplinary/${id}`),
  listGrievances:   (p) => api.get('/grievances', { params: p }),
  fileGrievance:    (d) => api.post('/grievances', d),
  reviewGrievance:  (id, d) => api.put(`/grievances/${id}`, d),
  removeGrievance:  (id) => api.delete(`/grievances/${id}`),
};

export const assetApi = {
  list:            (p) => api.get('/assets', { params: p }),
  create:          (d) => api.post('/assets', d),
  update:          (id, d) => api.put(`/assets/${id}`, d),
  remove:          (id) => api.delete(`/assets/${id}`),
  listAllocations: (p) => api.get('/assets/allocations', { params: p }),
  allocate:        (d) => api.post('/assets/allocations', d),
  returnAsset:     (id, d) => api.put(`/assets/allocations/${id}`, d),
  removeAllocation:(id) => api.delete(`/assets/allocations/${id}`),
};

export const clearanceApi = {
  list:             (p) => api.get('/clearances', { params: p }),
  getEmployeeAssets:(empId) => api.get(`/clearances/employee/${encodeURIComponent(empId)}/assets`),
  trigger:          (d) => api.post('/clearances', d),
  update:           (id, d) => api.put(`/clearances/${id}`, d),
  toggleItem:       (id, completed, notes) => api.patch(`/clearances/items/${id}`, { completed, notes }),
  remove:           (id) => api.delete(`/clearances/${id}`),
};

export const medicalApi = {
  list:    (p) => api.get('/medical', { params: p }),
  create:  (d) => api.post('/medical', d),
  update:  (id, d) => api.put(`/medical/${id}`, d),
  remove:  (id) => api.delete(`/medical/${id}`),
};

export const gatepassApi = {
  list:    (p) => api.get('/gatepass', { params: p }),
  create:  (d) => api.post('/gatepass', d),
  update:  (id, d) => api.put(`/gatepass/${id}`, d),
  remove:  (id) => api.delete(`/gatepass/${id}`),
};

export const memosApi = {
  list:        (p) => api.get('/memos', { params: p }),
  create:      (d) => api.post('/memos', d),
  update:      (id, d) => api.put(`/memos/${id}`, d),
  remove:      (id) => api.delete(`/memos/${id}`),
  acknowledge: (id) => api.post(`/memos/${id}/acknowledge`),
  getAcks:     (id) => api.get(`/memos/${id}/acknowledgements`),
};

export const wagesApi = {
  list:        (p) => api.get('/wages', { params: p }),
  getItems:    (id) => api.get(`/wages/${id}/items`),
  create:      (d) => api.post('/wages', d),
  updateStatus:(id, status) => api.put(`/wages/${id}`, { status }),
  remove:      (id) => api.delete(`/wages/${id}`),
};

export const loansApi = {
  list:            (p) => api.get('/loans', { params: p }),
  create:          (d) => api.post('/loans', d),
  update:          (id, d) => api.put(`/loans/${id}`, d),
  remove:          (id) => api.delete(`/loans/${id}`),
  listAdvances:    (p) => api.get('/loans/advances', { params: p }),
  createAdvance:   (d) => api.post('/loans/advances', d),
  updateAdvance:   (id, d) => api.put(`/loans/advances/${id}`, d),
  removeAdvance:   (id) => api.delete(`/loans/advances/${id}`),
  getStatement:    (id) => api.get(`/loans/${id}/statement`),
};

export const promotionsApi = {
  list:   (p) => api.get('/promotions', { params: p }),
  create: (d) => api.post('/promotions', d),
  review: (id, d) => api.put(`/promotions/${id}`, d),
  remove: (id) => api.delete(`/promotions/${id}`),
};

export const probationApi = {
  listEmployees: (p) => api.get('/probation/employees', { params: p }),
  submitReview:  (d) => api.post('/probation/reviews', d),
  listReviews:   (empId) => api.get(`/probation/reviews/${encodeURIComponent(empId)}`),
  checkAlerts:   () => api.post('/probation/check-alerts'),
};

export const anomaliesApi = {
  list:     (p) => api.get('/anomalies', { params: p }),
  detect:   (d) => api.post('/anomalies/detect', d),
  resolve:  (id, d) => api.put(`/anomalies/${id}/resolve`, d),
  getStats: () => api.get('/anomalies/stats'),
};

export const shiftApi = {
  listPatterns:     () => api.get('/shifts/patterns'),
  createPattern:    (d) => api.post('/shifts/patterns', d),
  listAssignments:  () => api.get('/shifts/assignments'),
  assignShift:      (d) => api.post('/shifts/assignments', d),
  removeAssignment: (id) => api.delete(`/shifts/assignments/${id}`),
};

export const overtimeApi = {
  list:    (p) => api.get('/overtime', { params: p }),
  create:  (d) => api.post('/overtime', d),
  approve: (id) => api.patch(`/overtime/${id}/approve`),
  verify:  (id) => api.patch(`/overtime/${id}/verify`),
};

export const announcementsApi = {
  list:   ()  => api.get('/announcements'),
  create: (d) => api.post('/announcements', d),
};

export const notificationsApi = {
  list:    (limit) => api.get('/notifications', { params: limit ? { limit } : {} }),
  markRead:    (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: ()   => api.patch('/notifications/read-all'),
};

export const ticketsApi = {
  list:   (p) => api.get('/tickets', { params: p }),
  create: (d) => api.post('/tickets', d),
  update: (id, d) => api.put(`/tickets/${id}`, d),
  remove: (id) => api.delete(`/tickets/${id}`),
};

export const exitRequestApi = {
  list:   (p) => api.get('/exit-requests', { params: p }),
  submit: (d) => api.post('/exit-requests', d),
  cancel: (id) => api.delete(`/exit-requests/${id}`),
};

export const getAvatarUrl = (user) => {
  if (!user || !user.profile_photo_url) return null;
  if (user.profile_photo_url.startsWith('http') || user.profile_photo_url.startsWith('data:')) {
    return user.profile_photo_url;
  }
  
  let baseUrl = '';
  if (process.env.REACT_APP_API_URL) {
    baseUrl = process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
  }
  
  if (user.profile_photo_url.startsWith('/')) {
    return `${baseUrl}${user.profile_photo_url}`;
  }
  return `${baseUrl}/uploads/documents/${user.profile_photo_url}`;
};

